/**
 * CSV export utilities for EcoEmpower analytics pages.
 *
 * All exports are client-side only — no backend call required.
 * Results are downloaded as a .csv file via a temporary anchor element.
 */

/**
 * Trigger a browser download of the given CSV string.
 *
 * @param {string} csvContent - Raw CSV text
 * @param {string} filename   - Download filename (should end with .csv)
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convert an array of objects (uniform keys) to CSV text.
 * All values are JSON-stringified so commas/quotes inside values are escaped.
 *
 * @param {Array<Object>} rows
 * @returns {string}
 */
export function arrayToCSV(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map(row =>
      headers.map(h => JSON.stringify(row[h] ?? '')).join(',')
    ),
  ];
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Per-page export helpers
// ---------------------------------------------------------------------------

/**
 * Export short-term forecast results (hourly).
 * Columns: hour, forecast_kwh, lower_bound_kwh, upper_bound_kwh
 *
 * @param {{ hours: number[], forecast: number[], lower: number[], upper: number[] }} chartData
 * @param {string} assetName  - Used in the filename
 * @param {string} targetDate - ISO date string, used in filename
 */
export function exportForecastShortTerm(chartData, assetName, targetDate) {
  const rows = (chartData.hours || []).map((h, i) => ({
    hour: h,
    forecast_kwh: chartData.forecast[i] ?? '',
    lower_bound_kwh: chartData.lower[i] ?? '',
    upper_bound_kwh: chartData.upper[i] ?? '',
  }));
  const safe = (assetName || 'forecast').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  downloadCSV(arrayToCSV(rows), `forecast_short_term_${safe}_${targetDate || 'export'}.csv`);
}

/**
 * Export long-term forecast results (monthly).
 * Columns: month, forecast_kwh, lower_bound_kwh, upper_bound_kwh
 *
 * @param {{ months: string[], forecast: number[], lower: number[], upper: number[] }} chartData
 * @param {string} assetName
 */
export function exportForecastLongTerm(chartData, assetName) {
  const rows = (chartData.months || []).map((m, i) => ({
    month: m,
    forecast_kwh: chartData.forecast[i] ?? '',
    lower_bound_kwh: chartData.lower[i] ?? '',
    upper_bound_kwh: chartData.upper[i] ?? '',
  }));
  const safe = (assetName || 'forecast').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(arrayToCSV(rows), `forecast_long_term_${safe}_${date}.csv`);
}

/**
 * Export day-ahead optimization results.
 * Produces two sections:
 *   1. KPI summary (key-value pairs)
 *   2. Hourly energy balance
 *
 * @param {{ baselineKPIs, optimizedKPIs, energyBalance }} mockResults
 * @param {string} targetDate - ISO date string
 * @param {string} currency
 */
export function exportOptimizationResults(mockResults, targetDate, currency) {
  const lines = [];

  // --- Section 1: KPI Summary ---
  lines.push('Section,Metric,Baseline,Optimized,Unit');

  const kpiPairs = [
    ['daily_cost', 'Daily Cost', currency],
    ['self_sufficiency_pct', 'Self-Sufficiency', '%'],
    ['self_consumption_pct', 'Self-Consumption', '%'],
    ['grid_import_kwh', 'Grid Import', 'kWh'],
    ['grid_import_cost_eur', 'Grid Import Cost', currency],
    ['grid_export_revenue_eur', 'Grid Export Revenue', currency],
    ['peak_import_kw', 'Peak Import', 'kW'],
    ['co2_avoided_kg', 'CO2 Avoided', 'kg'],
  ];

  const b = mockResults.baselineKPIs || {};
  const o = mockResults.optimizedKPIs || {};

  kpiPairs.forEach(([key, label, unit]) => {
    const bVal = b[key] != null ? b[key].toFixed(2) : '';
    const oVal = o[key] != null ? o[key].toFixed(2) : '';
    lines.push(`KPI,${JSON.stringify(label)},${bVal},${oVal},${unit}`);
  });

  // --- Section 2: Hourly Energy Balance ---
  lines.push('');
  lines.push('Hour,Load_kWh,PV_kWh,Grid_Import_kWh,Grid_Export_kWh,Battery_Charge_kWh,Battery_Discharge_kWh');

  const eb = mockResults.energyBalance || {};
  const hours = Array.from({ length: 24 }, (_, i) => i);
  hours.forEach(h => {
    const row = [
      h,
      eb.load?.[h] ?? '',
      eb.pv?.[h] ?? '',
      eb.gridImport?.[h] ?? '',
      eb.gridExport?.[h] ?? '',
      eb.batteryCharge?.[h] ?? '',
      eb.batteryDischarge?.[h] ?? '',
    ];
    lines.push(row.join(','));
  });

  downloadCSV(lines.join('\n'), `optimization_results_${targetDate || 'export'}.csv`);
}

/**
 * Export CBA results.
 * Produces two sections:
 *   1. Scenario summary (NPV, BCR, payback, annual savings, CO2)
 *   2. Cumulative cash flow per year
 *
 * @param {{ scenarios, cumulativeCashFlow, sensitivity }} results
 * @param {string} currency
 */
export function exportCBAResults(results, currency) {
  if (!results) return;
  const lines = [];
  const sym = currency || 'EUR';

  // --- Section 1: Scenario Summary ---
  lines.push(`Scenario,NPV (${sym}),BCR,Payback (years),Annual Savings (${sym}),CO2 Avoided (t/yr)`);
  (results.scenarios || []).forEach(s => {
    lines.push([
      JSON.stringify(s.name || ''),
      s.npv ?? '',
      s.bcr != null ? s.bcr.toFixed(2) : '',
      s.payback != null ? s.payback.toFixed(1) : 'N/A',
      s.annualSavings ?? '',
      s.co2Avoided != null ? s.co2Avoided.toFixed(2) : '',
    ].join(','));
  });

  // --- Section 2: Cumulative Cash Flow ---
  const ccf = results.cumulativeCashFlow || {};
  if ((ccf.years || []).length > 0) {
    lines.push('');
    lines.push(`Year,No Intervention (${sym}),PV Only (${sym}),PV+BESS (${sym})`);
    (ccf.years || []).forEach((yr, i) => {
      lines.push([
        yr,
        ccf.baseline?.[i] ?? '',
        ccf.pvOnly?.[i] ?? '',
        ccf.pvBess?.[i] ?? '',
      ].join(','));
    });
  }

  const date = new Date().toISOString().split('T')[0];
  downloadCSV(lines.join('\n'), `cba_results_${date}.csv`);
}

/**
 * Export long-term KPI trajectory results.
 * Sections: KPI trajectories, energy flows, savings breakdown, baseline comparison, summary.
 *
 * @param {object} results - With-investment results
 * @param {string} currency
 * @param {object} [baseline] - Optional existing-only baseline results
 */
export function exportLongTermResults(results, currency, baseline) {
  if (!results) return;
  const lines = [];
  const sym = currency || 'EUR';
  const n = (results.years || []).length;

  // --- Section 1: Annual KPI Trajectories ---
  const kpiHeader = [
    'Year', 'Self-Sufficiency (%)', 'Self-Consumption (%)', 'Grid-Dependence (%)',
    `Cumulative Savings (${sym})`, `Avoided Import Cost (${sym})`, `Export Revenue (${sym})`,
  ];
  if (baseline) {
    kpiHeader.push(
      'Self-Sufficiency Existing (%)', 'Grid-Dependence Existing (%)',
      `Cumulative Savings Existing (${sym})`, `Avoided Import Existing (${sym})`, `Export Revenue Existing (${sym})`,
    );
  }
  lines.push(kpiHeader.join(','));

  for (let i = 0; i < n; i++) {
    const yr = results.years[i];
    const row = [
      yr,
      results.selfSufficiency?.[i]?.toFixed(1) ?? '',
      results.selfConsumption?.[i]?.toFixed(1) ?? '',
      results.gridDependence?.[i]?.toFixed(1) ?? '',
      results.cumulativeSavings?.[i] ?? '',
      results.cumulativeSCSavings?.[i] ?? '',
      results.cumulativeExportRevenue?.[i] ?? '',
    ];
    if (baseline) {
      row.push(
        baseline.selfSufficiency?.[i]?.toFixed(1) ?? '',
        baseline.gridDependence?.[i]?.toFixed(1) ?? '',
        baseline.cumulativeSavings?.[i] ?? '',
        baseline.cumulativeSCSavings?.[i] ?? '',
        baseline.cumulativeExportRevenue?.[i] ?? '',
      );
    }
    lines.push(row.join(','));
  }

  // --- Section 2: Annual Energy Flows ---
  lines.push('');
  const flowHeader = [
    'Year', 'Self-Consumed (kWh)', 'Grid Import (kWh)', 'Grid Export (kWh)', 'Total Load (kWh)',
  ];
  if (baseline) {
    flowHeader.push(
      'Self-Consumed Existing (kWh)', 'Grid Import Existing (kWh)', 'Grid Export Existing (kWh)',
    );
  }
  lines.push(flowHeader.join(','));

  for (let i = 0; i < n; i++) {
    const row = [
      results.years[i],
      results.selfConsumedKwh?.[i]?.toFixed(0) ?? '',
      results.gridImportKwh?.[i]?.toFixed(0) ?? '',
      results.gridExportKwh?.[i]?.toFixed(0) ?? '',
      results.loadKwh?.[i]?.toFixed(0) ?? '',
    ];
    if (baseline) {
      row.push(
        baseline.selfConsumedKwh?.[i]?.toFixed(0) ?? '',
        baseline.gridImportKwh?.[i]?.toFixed(0) ?? '',
        baseline.gridExportKwh?.[i]?.toFixed(0) ?? '',
      );
    }
    lines.push(row.join(','));
  }

  // --- Section 3: Summary ---
  const s = results.summary || {};
  lines.push('');
  lines.push('Summary Metric,With Investment,Existing Only,Unit');
  const bs = baseline?.summary || {};
  lines.push(`Total Savings,${s.totalSavings ?? ''},${bs.totalSavings ?? ''},${sym}`);
  lines.push(`Avg Self-Sufficiency,${s.avgSelfSufficiency?.toFixed(1) ?? ''},${bs.avgSelfSufficiency?.toFixed(1) ?? ''},%`);
  lines.push(`Total CO2 Avoided,${s.totalCO2Avoided?.toFixed(2) ?? ''},${bs.totalCO2Avoided?.toFixed(2) ?? ''},tonnes`);
  lines.push(`ROI,${s.roi?.toFixed(1) ?? ''},${bs.roi?.toFixed(1) ?? ''},%`);

  const date = new Date().toISOString().split('T')[0];
  downloadCSV(lines.join('\n'), `longterm_kpi_results_${date}.csv`);
}
